import React, { useEffect, useMemo, useState, useCallback } from 'react'
import { serverListSlice } from 'infra/redux/reducers'
import * as Yup from 'yup'
import { confirmUniqueness, enqueueNotification, getUserInfo } from 'Utils/Helpers'
import { useFormik } from 'formik'
import CommandViewer from '../CreateModal/Command/CommandViewer'
import ProxyTokenHelp from '../CreateModal/Command/ProxyTokenHelp'
import { Button, TextInput, Typography } from 'procyon-ui'
import { ApiProvider, useDispatch } from 'Core'

const DockerModal = ({ open, onClose }) => {
  const dispatch = useDispatch()
  const [command, setCommand] = useState(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    dispatch(serverListSlice.thunk())
  }, [dispatch])

  const InitialValues = useMemo(
    () => ({
      ClusterID: '',
      Hostname: '',
      _ProxyType: 'PrivateProxy'
    }),
    []
  )

  const VALIDATION_RULES = useMemo(
    () =>
      Yup.object({
        ClusterID: Yup.string()
          .test('confirmUniqueness', '*This Cluster ID is already taken', (value) =>
            confirmUniqueness(value, 'proxyList', (e) => e.Status.ClusterID)
          )
          .required('*Cluster ID is required'),
        Hostname: Yup.string().when('_ProxyType', {
          is: 'Proxy Token',
          then: Yup.string().notRequired(),
          otherwise: Yup.string().required(({ parent }) =>
            parent._ProxyType === 'PrivateProxy' ? '*Host IP is required' : '*Host Name is required'
          )
        })
      }),
    []
  )

  const formik = useFormik({
    initialValues: InitialValues,
    validationSchema: VALIDATION_RULES,
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit: async (fields, formikHelpers) => {
      try {
        setLoading(true)
        const ProxyType = ['PrivateProxy', 'Proxy Token'].includes(fields._ProxyType)
          ? 'PrivateProxy'
          : 'Relay'

        const formPayload = {
          ObjectMeta: {
            Tenant: await getUserInfo().tenant,
            Namespace: await getUserInfo().org
          },
          ClusterID: fields.ClusterID,
          ProxyType
        }

        const response = await new ApiProvider('proxytokens').setInstance(formPayload).post()
        if (response) {
          let cmd = `docker run -itd -e ENV_PROXY_TOKEN=${response.data.ProxyToken} --net=host --name procyon-proxy -v /var/log/procyon:/var/log/procyon -v /var/lib/procyon/ssl:/procyon/ssl -v /var/lib/procyon/recordings:/procyon/recordings public.ecr.aws/v2s2h8i9/procyon-proxy /procyon/bin/cyonproxy -hostname ${fields.Hostname}`

          if (fields._ProxyType === 'TargetRelay') {
            cmd = `docker run -itd -e ENV_PROXY_TOKEN=${response.data.ProxyToken} --net=host --name procyon-target -v /var/log/procyon:/var/log/procyon -v /var/lib/procyon/ssl:/procyon/ssl -v /var/lib/procyon:/var/lib/procyon -v /etc/ssh/sshd_config:/etc/ssh/sshd_config public.ecr.aws/v2s2h8i9/procyon-target /procyon/bin/cyontarget -hostname ${fields.Hostname}`
          }

          if (fields._ProxyType === 'Proxy Token') {
            cmd = `${response.data.ProxyToken}`
          }

          setCommand({ command: cmd, type: fields._ProxyType })
          enqueueNotification('Docker command generated', 'info')
          formikHelpers.resetForm({ values: InitialValues })
        }
      } catch (error) {
        console.error('error in proxytokens onSubmit', error)
        enqueueNotification('Operation failed', 'error')
      } finally {
        setLoading(false)
      }
    }
  })

  const onCancel = () => {
    formik.resetForm({ values: InitialValues })
    setCommand(null)
    onClose()
  }

  const handleOnClose = () => {
    setCommand(null)
    onClose()
  }

  const handleProxyRadioChange = (value) => {
    formik.setFieldValue('_ProxyType', value)
  }

  const proxyOptions = [
    { label: 'Private Proxy', value: 'PrivateProxy' },
    { label: 'Relay', value: 'Relay' },
    { label: 'Proxy Token', value: 'Proxy Token' }
  ]

  // Reset when the modal opens
  useEffect(() => {
    if (open) {
      setCommand(null)
      formik.resetForm({ values: InitialValues })
    }
  }, [open])

  if (!open) return null

  return (
    <div className='fixed inset-0 z-[1000] flex items-center justify-center'>
      <div className='absolute inset-0 bg-black/50 backdrop-blur-[1px]' onClick={handleOnClose} />
      <div
        className='relative p-4 z-[1001] w-[min(96vw,720px)] rounded-[5px] max-h-[100vh] overflow-y-auto bg-[var(--color-common-bg-base)] shadow-2xl ring-1 ring-black/5'
        onClick={(e) => e.stopPropagation()}
      >
        <div className='flex items-start justify-between border-b border-gray-100 px-3 py-4'>
          <Typography variant='h3'>Setup Gateways</Typography>
          <Button variant='gray' onClick={handleOnClose}>
            Close
          </Button>
        </div>

        <div className='my-3 px-3'>
          {command ? (
            <CommandViewer
              actionLabel={
                command.type === 'Proxy Token' ? 'Copy Proxy Token' : 'Copy Docker Command'
              }
              copyMessage={
                command.type === 'Proxy Token'
                  ? 'Proxy Token copied to clipboard!'
                  : 'Docker command copied to clipboard!'
              }
              command={command.command}
            />
          ) : (
            <>
              <div className='mb-4'>
                <div className='flex gap-4'>
                  {proxyOptions.map((opt) => (
                    <div
                      key={opt.value}
                      onClick={() => handleProxyRadioChange(opt.value)}
                      className={`cursor-pointer rounded-lg border px-4 py-2 text-sm font-medium transition-all duration-150 ${
                        formik.values._ProxyType === opt.value
                          ? 'border-[var(--color-button-primary-border)] bg-[var(--color-button-primary-bg)] text-[var(--color-button-primary-text)]'
                          : 'border-gray-300 text-gray-700 hover:border-[var(--color-button-primary-hover-border)]'
                      }`}
                    >
                      {opt.label}
                    </div>
                  ))}
                </div>
              </div>

              <div className='mb-4'>
                <TextInput
                  {...formik.getFieldProps('ClusterID')}
                  label='Cluster ID *'
                  required
                  sx={{ width: '100%' }}
                  fullWidth
                  error={!!formik.errors.ClusterID}
                  className='mb-2'
                />
                {formik.errors.ClusterID && !formik.touched.ClusterID && (
                  <div className='text-red-600 text-sm'>{formik.errors.ClusterID}</div>
                )}
              </div>

              {formik.values._ProxyType === 'PrivateProxy' && (
                <div className='mb-4'>
                  <TextInput
                    {...formik.getFieldProps('Hostname')}
                    label='Host IP *'
                    required
                    sx={{ width: '100%' }}
                    fullWidth
                    error={!!formik.errors.Hostname}
                    className='mb-2'
                  />
                  {formik.errors.Hostname && !formik.touched.Hostname && (
                    <div className='text-red-600 text-sm'>{formik.errors.Hostname}</div>
                  )}
                </div>
              )}

              {formik.values._ProxyType !== 'PrivateProxy' &&
                formik.values._ProxyType !== 'Proxy Token' && (
                  <div className='mb-4'>
                    <TextInput
                      {...formik.getFieldProps('Hostname')}
                      label='Host Name *'
                      required
                      sx={{ width: '100%' }}
                      fullWidth
                      error={!!formik.errors.Hostname}
                      className='mb-2'
                    />
                    {formik.errors.Hostname && !formik.touched.Hostname && (
                      <div className='text-red-600 text-sm'>{formik.errors.Hostname}</div>
                    )}
                  </div>
                )}

              {formik.values._ProxyType === 'Proxy Token' && <ProxyTokenHelp />}
            </>
          )}
        </div>

        <div className='mt-auto px-3 pb-4 flex items-center justify-end space-x-2'>
          <Button variant='gray' onClick={onCancel}>
            Cancel
          </Button>
          {!command && (
            <Button
              variant='primary'
              onClick={() => formik.handleSubmit()}
              className='flex items-center bg-blue-600 hover:bg-blue-700 text-white'
              disabled={loading}
            >
              {loading && (
                <svg
                  className='animate-spin h-5 w-5 mr-2 text-white'
                  xmlns='http://www.w3.org/2000/svg'
                  fill='none'
                  viewBox='0 0 24 24'
                >
                  <circle
                    className='opacity-25'
                    cx='12'
                    cy='12'
                    r='10'
                    stroke='currentColor'
                    strokeWidth='4'
                  />
                  <path className='opacity-75' fill='currentColor' d='M4 12a8 8 0 018-8v8H4z' />
                </svg>
              )}
              <span className='ml-1'>
                {formik.values._ProxyType === 'Proxy Token'
                  ? 'Generate Token'
                  : 'Get Docker Command'}
              </span>
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}

export default DockerModal
